import { type UserDistrictSummary } from "@crowdcoursing/api/openapi";
import { Search as SearchIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import { studentDisplayNameLastFirst, studentInitials } from "~ui/pages/VisitorManagement/visitorManagementDisplay";
import { makeProfileAvatarUrl } from "~utils/url";

const tFacultySearchLabel = "Search";
const tFacultySearchPlaceholder = "Name or email";
const tClose = "Close";
const tSignIn = "Sign In";
const tSigningIn = "Signing in…";
const tStudentModalTitle = "Select student";
const tStudentModalSubtitle = "Search for a student by name or email, then choose them to sign in or sign out.";
const tStudentTypeToSearch = "Type at least one character to see matches.";
const tNoStudents = "No students are enrolled for this school.";
const tNoStudentMatches = "No students match your search.";
const tSelfSignOut = "Self Sign Out";
const tAddParent = "Add Parent";
const tSignOutOptionsHeading = "Sign out";
const tBack = "Back";
const tParentsLoadError = "Could not load parents.";
const tSignOut = "Sign Out";
const tSigningOut = "Signing out…";
const tBackToList = "Back to list";

export interface VisitorManagementStudentDialogProps {
  closeStudentModal: () => void;
  dialogButtonSize: "large" | "medium";
  dialogFullScreen: boolean;
  dialogTitleTypography: "h5" | "h6";
  filteredStudents: UserDistrictSummary[];
  handleSignOutStudent: (parentUserIdForQuery?: string) => void | Promise<void>;
  handleStudentSignIn: () => void | Promise<void>;
  isKiosk: boolean;
  isSignInPending: boolean;
  isSignOutPending: boolean;
  listAvatarSize: number;
  listItemSx: SxProps<Theme>;
  maxWidth: "md" | "sm";
  parentOptionsForStudent: { id: string; name: string }[];
  setStudentForAction: (user: UserDistrictSummary | null) => void;
  setStudentSearch: (value: string) => void;
  setStudentSignOutOptionsOpen: (open: boolean) => void;
  sortedStudents: UserDistrictSummary[];
  studentForAction: UserDistrictSummary | null;
  studentModalOpen: boolean;
  studentParentsStatus: "error" | "pending" | "success";
  studentSearch: string;
  studentSearchActive: boolean;
  studentSignOutOptionsOpen: boolean;
  studentUsersStatus: "error" | "pending" | "success";
}

export function VisitorManagementStudentDialog({
  closeStudentModal,
  dialogButtonSize,
  dialogFullScreen,
  dialogTitleTypography,
  filteredStudents,
  handleSignOutStudent,
  handleStudentSignIn,
  isKiosk,
  isSignInPending,
  isSignOutPending,
  listAvatarSize,
  listItemSx,
  maxWidth,
  parentOptionsForStudent,
  setStudentForAction,
  setStudentSearch,
  setStudentSignOutOptionsOpen,
  sortedStudents,
  studentForAction,
  studentModalOpen,
  studentParentsStatus,
  studentSearch,
  studentSearchActive,
  studentSignOutOptionsOpen,
  studentUsersStatus,
}: VisitorManagementStudentDialogProps) {
  return (
    <Dialog
      fullScreen={dialogFullScreen}
      fullWidth
      maxWidth={maxWidth}
      open={studentModalOpen}
      onClose={closeStudentModal}
    >
      {studentForAction ? (
        studentSignOutOptionsOpen ? (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack alignItems="center" direction="row" spacing={2}>
                <Avatar
                  alt=""
                  src={studentForAction.userId ? makeProfileAvatarUrl(studentForAction.userId) : undefined}
                  sx={{ height: isKiosk ? 72 : 56, width: isKiosk ? 72 : 56 }}
                >
                  {studentInitials(studentForAction)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap fontWeight={600} variant={dialogTitleTypography}>
                    {studentDisplayNameLastFirst(studentForAction)}
                  </Typography>
                  {studentForAction.email ? (
                    <Typography color="text.secondary" noWrap variant={isKiosk ? "body1" : "body2"}>
                      {studentForAction.email}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
              <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                <Button
                  color="inherit"
                  size={dialogButtonSize}
                  sx={{ alignSelf: "flex-start" }}
                  variant="text"
                  onClick={() => setStudentSignOutOptionsOpen(false)}
                >
                  {tBack}
                </Button>
                <Typography color="text.secondary" variant={isKiosk ? "h6" : "subtitle2"}>
                  {tSignOutOptionsHeading}
                </Typography>
                {studentParentsStatus === "error" ? (
                  <Typography color="error" variant="body2">
                    {tParentsLoadError}
                  </Typography>
                ) : null}
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    disabled={isSignOutPending || !studentForAction.userId}
                    size={dialogButtonSize}
                    variant="outlined"
                    onClick={() => {
                      void handleSignOutStudent();
                    }}
                  >
                    {isSignOutPending ? tSigningOut : tSelfSignOut}
                  </Button>
                  {studentParentsStatus === "pending" ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      <CircularProgress aria-label="Loading parents" size={isKiosk ? 40 : 28} />
                    </Box>
                  ) : parentOptionsForStudent.length > 0 ? (
                    parentOptionsForStudent.map((parent) => (
                      <Button
                        key={parent.id}
                        fullWidth
                        disabled={isSignOutPending}
                        size={dialogButtonSize}
                        variant="outlined"
                        onClick={() => {
                          void handleSignOutStudent(parent.id);
                        }}
                      >
                        {parent.name}
                      </Button>
                    ))
                  ) : (
                    <Button
                      fullWidth
                      size={dialogButtonSize}
                      variant="outlined"
                      onClick={() => {
                        /* Add parent — wire when flow exists */
                      }}
                    >
                      {tAddParent}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button size={dialogButtonSize} onClick={closeStudentModal}>
                {tClose}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack alignItems="center" direction="row" spacing={2}>
                <Avatar
                  alt=""
                  src={studentForAction.userId ? makeProfileAvatarUrl(studentForAction.userId) : undefined}
                  sx={{ height: isKiosk ? 72 : 56, width: isKiosk ? 72 : 56 }}
                >
                  {studentInitials(studentForAction)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap fontWeight={600} variant={dialogTitleTypography}>
                    {studentDisplayNameLastFirst(studentForAction)}
                  </Typography>
                  {studentForAction.email ? (
                    <Typography color="text.secondary" noWrap variant={isKiosk ? "body1" : "body2"}>
                      {studentForAction.email}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                <Button
                  color="inherit"
                  size={dialogButtonSize}
                  sx={{ alignSelf: "flex-start" }}
                  variant="text"
                  onClick={() => {
                    setStudentForAction(null);
                    setStudentSignOutOptionsOpen(false);
                  }}
                >
                  {tBackToList}
                </Button>
                <Stack direction={{ sm: "row", xs: "column" }} spacing={2}>
                  <Button
                    fullWidth
                    disabled={isSignInPending || !studentForAction.userId}
                    size={dialogButtonSize}
                    variant="contained"
                    onClick={() => {
                      void handleStudentSignIn();
                    }}
                  >
                    {isSignInPending ? tSigningIn : tSignIn}
                  </Button>
                  <Button
                    fullWidth
                    size={dialogButtonSize}
                    variant="outlined"
                    onClick={() => setStudentSignOutOptionsOpen(true)}
                  >
                    {tSignOut}
                  </Button>
                </Stack>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button size={dialogButtonSize} onClick={closeStudentModal}>
                {tClose}
              </Button>
            </DialogActions>
          </>
        )
      ) : (
        <>
          <DialogTitle>{tStudentModalTitle}</DialogTitle>
          <DialogContent dividers>
            <Typography color="text.secondary" sx={{ mb: 2 }} variant={isKiosk ? "body1" : "body2"}>
              {tStudentModalSubtitle}
            </Typography>
            <TextField
              fullWidth
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize={isKiosk ? "medium" : "small"} />
                  </InputAdornment>
                ),
              }}
              label={tFacultySearchLabel}
              placeholder={tFacultySearchPlaceholder}
              size={isKiosk ? "medium" : "small"}
              sx={{ mb: 2 }}
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
            />
            {studentUsersStatus === "pending" ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress aria-label="Loading students" />
              </Box>
            ) : studentUsersStatus === "error" ? (
              <Typography color="error">Could not load students.</Typography>
            ) : !studentSearchActive ? (
              <Typography color="text.secondary" variant={isKiosk ? "body1" : "body2"}>
                {tStudentTypeToSearch}
              </Typography>
            ) : sortedStudents.length === 0 ? (
              <Typography color="text.secondary">{tNoStudents}</Typography>
            ) : filteredStudents.length === 0 ? (
              <Typography color="text.secondary">{tNoStudentMatches}</Typography>
            ) : (
              <List disablePadding>
                {filteredStudents.map((user) => {
                  const uid = user.userId ?? "";
                  const name = studentDisplayNameLastFirst(user);
                  return (
                    <ListItem key={uid || name} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        alignItems="center"
                        disabled={isSignInPending || isSignOutPending}
                        onClick={() => {
                          setStudentForAction(user);
                          setStudentSignOutOptionsOpen(false);
                        }}
                        sx={listItemSx}
                      >
                        <ListItemAvatar
                          sx={{
                            alignSelf: "center",
                            marginInlineEnd: 0,
                            minWidth: isKiosk ? 72 : 56,
                          }}
                        >
                          <Avatar
                            alt=""
                            src={uid ? makeProfileAvatarUrl(uid) : undefined}
                            sx={{ height: listAvatarSize, width: listAvatarSize }}
                          >
                            {studentInitials(user)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={name}
                          secondary={user.email ?? undefined}
                          primaryTypographyProps={{ fontWeight: 600, variant: isKiosk ? "body1" : "body2" }}
                          secondaryTypographyProps={{ component: "span", variant: "body2" }}
                          sx={{
                            "& .MuiListItemText-primary": { lineHeight: 1.35 },
                            "& .MuiListItemText-secondary": { mt: 0.25 },
                            margin: 0,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button size={dialogButtonSize} onClick={closeStudentModal}>
              {tClose}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
